import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";
import { userKeys } from "./queryKeys";
import type { EmailSignature } from "../../utils/types";

type UpdateEmailSignatureVariables = {
  text?: string | null;
  image_url?: string | null;
  enabled?: boolean;
};

type UploadSignatureImageResponse = {
  image_url: string;
};

export const useEmailSignatureQuery = () =>
  useQuery({
    queryKey: userKeys.emailSignature,
    queryFn: async () => {
      const { data } = await apiClient.get<EmailSignature>(
        "/user/email_signature",
      );
      return data;
    },
  });

export const useUpdateEmailSignatureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateEmailSignatureVariables) => {
      const { data } = await apiClient.patch<EmailSignature>(
        "/user/email_signature",
        variables,
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: userKeys.emailSignature,
      });
    },
  });
};

export const useUploadSignatureImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await apiClient.post<UploadSignatureImageResponse>(
        "/user/email_signature/upload_image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: userKeys.emailSignature,
      });
    },
  });
};

export const useLinkMicrosoftOAuthMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string; state: string }) => {
      const { data } = await apiClient.post("/user/microsoft_oauth", payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.mailboxSyncStatus });
    },
  });
};

export type UserInfo = {
  username: string;
  oauth_provider: string | null;
  email: string | null;
};

export const useUserInfoQuery = () => {
  return useQuery({
    queryKey: userKeys.info,
    queryFn: async () => {
      const { data } = await apiClient.get<UserInfo>("/user/info");
      return data;
    },
  });
};

export const useUpdateUsernameMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (username: string) => {
      const { data } = await apiClient.patch<UserInfo>("/user/username", {
        username,
      });
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.info });
    },
  });
};

export const useUserPageScopesQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["user", "page-scopes"],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<{ page_scopes: string[] }>(
        "/user/page_scopes",
      );
      return data.page_scopes;
    },
  });

export type MailboxSyncStatus = {
  sync_state: "IDLE" | "SYNCING" | "ERROR";
  integration_status: "ACTIVE" | "EXPIRED" | "REVOKED";
  last_successful_sync_at: string | null;
  last_attempted_sync_at: string | null;
  last_error: string | null;
  has_error: boolean;
};

export const useMailboxSyncStatusQuery = () => {
  return useQuery({
    queryKey: userKeys.mailboxSyncStatus,
    queryFn: async () => {
      const { data } = await apiClient.get<MailboxSyncStatus>(
        "/user/mailbox_sync_status",
      );
      return data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.sync_state === "SYNCING") {
        return 2000;
      }
      return 300000; // Poll every 5 minutes when idle
    },
  });
};

export type TriggerMailboxSyncResponse = {
  status: "started" | "already_syncing" | "skipped";
  job_id?: string | null;
  reason?: string;
};

export type DeleteUserDataScope = "user" | "all";

export type DeleteUserDataResponse = {
  status: "success";
  deleted_scope: DeleteUserDataScope;
  deleted_user_count: number;
};

export const useTriggerMailboxSyncMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<TriggerMailboxSyncResponse>(
        "/user/trigger_mailbox_sync",
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.mailboxSyncStatus });
    },
  });
};

export const useDeleteUserDataMutation = () =>
  useMutation({
    mutationFn: async (scope: DeleteUserDataScope) => {
      const { data } = await apiClient.delete<DeleteUserDataResponse>(
        "/user/data",
        { data: { scope } },
      );
      return data;
    },
  });

export type OutlookSubscriptionDebug = {
  applicable: boolean;
  reason?: string;
  mailbox_user_id?: number;
  acting_user_is_mailbox_owner?: boolean;
  watch_configured?: boolean;
  subscription_id?: string | null;
  expiration_utc?: string | null;
  is_expired?: boolean;
  notification_url?: string | null;
  last_setup_at?: string | null;
  last_setup_success?: boolean | null;
  last_setup_error?: string | null;
  last_notification_at?: string | null;
  last_notification_enqueued_at?: string | null;
  last_notification_enqueue_error?: string | null;
  last_push_processed_at?: string | null;
  last_push_processing_success?: boolean | null;
  last_push_processing_error?: string | null;
};

export const useOutlookSubscriptionDebugQuery = (enabled: boolean) =>
  useQuery({
    queryKey: ["outlook", "subscription-debug"],
    queryFn: async () => {
      const { data } = await apiClient.get<OutlookSubscriptionDebug>(
        "/outlook/subscription_debug",
      );
      return data;
    },
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });
